import {
  ModelError_Response,
  SettlementExportRendererKey,
  SettlementExportStatus,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useContext } from 'react';

import FileDownloadResultContext, {
  FileDownloadOutcome,
} from '~contexts/FileDownloadResultContext';
import { SettlementExport } from '~hooks/useSettlementExports/models';
import useSettlementExports from '~hooks/useSettlementExports/useSettlementExports';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import isEnumValue from '~utils/isEnumValue';
import { downloadFile } from '~utils/utilFunctions';

// A batch holds at most 1,000 settlements. Five minutes covers rendering a full
// batch plus queue delay; past that the broadcast is treated as missed and the
// batch status is read instead.
const SETTLEMENT_EXPORT_TIMEOUT_IN_MS = 5 * 60 * 1000;
// Once the broadcast wait has timed out, the batch status is read on this interval for
// up to 10 more minutes (15 in all); past that the page stops tracking it and the
// Exported column is where its result shows up.
const SETTLEMENT_EXPORT_STATUS_POLL_INTERVAL_IN_MS = 30 * 1000;
const MAX_SETTLEMENT_EXPORT_STATUS_READS = 20;

const waitFor = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });

interface StartSettlementExportParams {
  companyId: string;
  rendererKey: SettlementExportRendererKey;
  settlementIds: string[];
}

// The API gives the batch a `file_url` but no file name, so take it from the URL path.
const getSettlementExportFileName = (fileUrl: string) => {
  const lastPathSegment = new URL(fileUrl).pathname.split('/').pop();

  return lastPathSegment ? decodeURIComponent(lastPathSegment) : 'settlement_export';
};

export default function useSettlementExportTracking() {
  const { toasterStore } = useStores();
  const { cancelFileDownload, enqueueFileDownload } = useContext(
    FileDownloadResultContext,
  );
  const { createSettlementExport, getSettlementExport } = useSettlementExports();

  const waitForSettlementExportFile = useCallback(
    (settlementExportId: string, onSubscribed: () => void) =>
      new Promise<FileDownloadOutcome>((resolve) => {
        enqueueFileDownload(settlementExportId, {
          onSettled: resolve,
          onSubscribed,
          shouldSuppressErrorToast: true,
          timeoutMs: SETTLEMENT_EXPORT_TIMEOUT_IN_MS,
        });
      }),
    [enqueueFileDownload],
  );

  const reportSettlementExportStatus = useCallback(
    (settlementExport: SettlementExport) => {
      const reportStatus = {
        [SettlementExportStatus.COMPLETED]: () => {
          if (settlementExport.fileUrl) {
            downloadFile(
              getSettlementExportFileName(settlementExport.fileUrl),
              settlementExport.fileUrl,
              true,
            );
          } else {
            toasterStore.push(
              alert(t('common.file_download.error_message'), AlertTypes.error),
            );
          }
        },
        [SettlementExportStatus.FAILED]: () => {
          toasterStore.push(
            alert(
              t('approvals.driver_pay.settlements.settlement_export.failed', {
                reason:
                  settlementExport.failureReason ??
                  t('common.file_download.error_message'),
              }),
              AlertTypes.error,
            ),
          );
        },
        [SettlementExportStatus.PENDING]: () => {
          toasterStore.push(
            alert(
              t('approvals.driver_pay.settlements.settlement_export.still_generating'),
              AlertTypes.info,
            ),
          );
        },
      } satisfies Record<SettlementExportStatus, () => void>;

      // The API can send a status newer than this build's spec.
      if (!isEnumValue(SettlementExportStatus, settlementExport.status)) {
        toasterStore.push(
          alert(t('common.file_download.error_message'), AlertTypes.error),
        );
        return;
      }

      reportStatus[settlementExport.status]();
    },
    [toasterStore],
  );

  // The broadcast is sent once and never replayed, so a batch that settles before the
  // subscription is confirmed would leave the page waiting out the full timeout.
  // Reading the status once the subscription is confirmed catches that; after a missed
  // broadcast the status endpoint (the API's documented fallback) is read until the
  // batch settles.
  const settleSettlementExport = useCallback(
    async (companyId: string, settlementExportId: string) => {
      let broadcastOutcome: FileDownloadOutcome | undefined;
      let markSubscribed: () => void = () => {};
      const subscribed = new Promise<void>((resolve) => {
        markSubscribed = resolve;
      });
      const trackBroadcast = async () => {
        broadcastOutcome = await waitForSettlementExportFile(
          settlementExportId,
          markSubscribed,
        );

        return broadcastOutcome;
      };
      const broadcastResult = trackBroadcast();

      // The wait times out into the broadcastResult branch if the cable never connects.
      await Promise.race([subscribed, broadcastResult]);

      try {
        const firstRead = await getSettlementExport({
          companyId,
          settlementExportId,
          shouldSuppressErrorToast: true,
        });
        const alreadySettled = firstRead.status !== SettlementExportStatus.PENDING;

        if (alreadySettled && broadcastOutcome === undefined) {
          cancelFileDownload(settlementExportId);
          reportSettlementExportStatus(firstRead);
          return;
        }
      } catch {
        // This read is only a shortcut; the broadcast or the polling below still settles.
      }

      const outcome = await broadcastResult;

      if (outcome === FileDownloadOutcome.SUCCESS) {
        return;
      }

      for (let readCount = 1; ; readCount += 1) {
        let settlementExport: SettlementExport;

        try {
          settlementExport = await getSettlementExport({ companyId, settlementExportId });
        } catch {
          // getSettlementExport has already toasted and reported the failed read.
          return;
        }

        const shouldReadAgain =
          settlementExport.status === SettlementExportStatus.PENDING &&
          readCount < MAX_SETTLEMENT_EXPORT_STATUS_READS;

        if (!shouldReadAgain) {
          reportSettlementExportStatus(settlementExport);
          return;
        }

        await waitFor(SETTLEMENT_EXPORT_STATUS_POLL_INTERVAL_IN_MS);
      }
    },
    [
      cancelFileDownload,
      getSettlementExport,
      reportSettlementExportStatus,
      waitForSettlementExportFile,
    ],
  );

  const trackSettlementExport = useCallback(
    async (companyId: string, settlementExportId: string) => {
      try {
        await settleSettlementExport(companyId, settlementExportId);
      } catch (error) {
        // Runs detached from the click that started it, so an unexpected failure is
        // reported here rather than rethrown to no one.
        try {
          connection.handleRequestError(error, t('common.file_download.error_message'));
        } catch {
          // handleRequestError always rethrows after toasting and reporting.
        }
      }
    },
    [settleSettlementExport],
  );

  const startSettlementExport = useCallback(
    async ({ companyId, rendererKey, settlementIds }: StartSettlementExportParams) => {
      try {
        const settlementExport = await createSettlementExport({
          companyId,
          rendererKey,
          settlementIds,
        });

        void trackSettlementExport(companyId, settlementExport.id);

        return true;
      } catch (error) {
        const isIneligibleSelectionError =
          isAxiosError<ModelError_Response>(error) && error.response?.status === 422;

        if (isIneligibleSelectionError) {
          const serverMessage = error.response?.data?.error?.errors?.[0]?.message;

          toasterStore.push(
            alert(
              serverMessage ?? t('error_messages.settlement_exports.failed_to_create'),
              AlertTypes.error,
            ),
          );
        }

        return false;
      }
    },
    [createSettlementExport, toasterStore, trackSettlementExport],
  );

  return { startSettlementExport };
}
